import { useMutation, useQuery } from '@tanstack/react-query';
import { getApiOrdersByEncryptedOrderIdDiscountClubsOptions } from 'api/generated/@tanstack/react-query.gen';
import { postApiOrdersByEncryptedOrderIdDiscountClubsByCouponTypeIdApply, postApiOrdersByEncryptedOrderIdDiscountClubsByCouponTypeIdPreview } from 'api/generated/sdk.gen';
import type { DiscountClubDto, DiscountClubFieldValue, OrderResponse } from 'api/generated/types.gen';
import { useAppState } from 'lib/app-state';

// Clubs by name, as last offered. The order names an applied club but carries
// neither its logo nor its form, and a club drops out of the offered list once
// none of its tickets are left, so it is kept from when it was last offered.
const knownClubs = new Map<string, DiscountClubDto>();
export const getKnownDiscountClub = (name?: string | null) => name ? knownClubs.get(name) : undefined;
export const getDiscountClubLogo = (name?: string | null) => getKnownDiscountClub(name)?.form?.logo ?? undefined;

// Changes whenever the order's tickets do, so the offered clubs are fetched
// again and a club with no ticket left to discount drops out.
const ticketsSignature = (order?: OrderResponse | null) => (order?.tickets ?? []).flatMap(group => group.tickets ?? []).map(ticket => `${ticket.encryptedId}:${ticket.ticketTypeId}:${ticket.discountClub?.card ?? ''}`).sort().join('|');

/** The discount clubs the order is offered, each with its card form. */
export const useDiscountClubs = (order?: OrderResponse | null, {
  enabled = true
}: {
  enabled?: boolean;
} = {}) => {
  const encryptedOrderId = order?.encryptedId ?? '';
  const options = getApiOrdersByEncryptedOrderIdDiscountClubsOptions({
    path: {
      encryptedOrderId
    }
  });
  const query = useQuery({
    ...options,
    queryKey: [...options.queryKey, ticketsSignature(order)] as unknown as typeof options.queryKey,
    enabled: enabled && !!encryptedOrderId,
    select: (clubs: DiscountClubDto[]) => {
      clubs.forEach(club => {
        if (club.name) knownClubs.set(club.name, club);
      });
      return clubs;
    }
  });
  return {
    ...query,
    clubs: query.data ?? []
  };
};
type CardVariables = {
  encryptedOrderId: string;
  couponTypeId: number;
  fields: DiscountClubFieldValue[];
};

/** Prices a card against the order's tickets. Nothing is saved. */
export const usePreviewDiscountClubCard = () => useMutation({
  mutationFn: ({
    encryptedOrderId,
    couponTypeId,
    fields
  }: CardVariables) => postApiOrdersByEncryptedOrderIdDiscountClubsByCouponTypeIdPreview({
    path: {
      encryptedOrderId,
      couponTypeId
    },
    body: {
      fields
    }
  }),
  // A declined card is shown in the dialog, not as the default error toast.
  onError: () => undefined
});

/**
 * Applies a card. The discounted tickets move to the club's ticket type and
 * price, and the returned order replaces the one in app state.
 */
export const useApplyDiscountClubCard = () => {
  const {
    setEventData
  } = useAppState();
  return useMutation({
    mutationFn: ({
      encryptedOrderId,
      couponTypeId,
      fields
    }: CardVariables) => postApiOrdersByEncryptedOrderIdDiscountClubsByCouponTypeIdApply({
      path: {
        encryptedOrderId,
        couponTypeId
      },
      body: {
        fields
      }
    }),
    // The order's new tickets change the offered clubs' query key, so they
    // are fetched again without an explicit invalidation.
    onSuccess: (data: any) => {
      if (data.status === 200 && data.data) {
        setEventData(current => ({
          ...current,
          order: data.data
        }));
      }
    },
    onError: () => undefined
  });
};